import { computed, inject, provide, Ref, ref } from "vue"
import { Event, Rsvp, RsvpReply } from "suus-api"
import { digestApi, rsvpApi } from "store/api"
import { EVENT_STATUS } from "constants/constants"
import store from "store"
import { FeatureFunctions } from "app/modules/feature.functions"
import { AxiosError } from "axios"

const EventFunctions = FeatureFunctions.EventFunctions

export interface OwnUserAttendance {
  initializeOwnUserStatus: (personal_record_id: string) => Promise<void>
  initializeOwnUserStatusFromDigest: () => Promise<void>
  ownUserId: Ref<string | null>
  ownUserStatusLoading: Ref<boolean>
  ownUserStatusError: Ref<string | null>
  ownUserAttends: Ref<boolean>
  ownUserGuestsCount: Ref<number>
  ownUserName: Ref<string>
  eventAttendStatus: Ref<EVENT_STATUS>
  attend: (guestsCount?: number) => Promise<void>
  attendFromDigest: (guestsCount?: number) => Promise<void>
  decline: () => Promise<void>
  declineFromDigest: () => Promise<void>
  checkAttendanceStatus: (ownUserStatus?: Rsvp) => void
  setEventAttendStatusDirectly: (eventStatus: EVENT_STATUS) => void
  updateAttendance: (
    sendAttendanceResponse: () => Promise<Rsvp>
  ) => Promise<void>
}

function OwnUserAttendance(eventId: string): OwnUserAttendance {
  const ownUserId: Ref<string | null> = ref<string | null>(null)

  const ownUserStatusLoading = ref(false)
  const ownUserStatusError: Ref<string | null> = ref(null)

  const eventAttendStatus = ref<EVENT_STATUS>(EVENT_STATUS.REGISTER)
  const ownUserAttends = ref<boolean>(false)
  const ownUserGuestsCount = ref<number>(0)
  const _ownUserStatus: Ref<Rsvp | null> = ref<Rsvp | null>(null)
  const ownUserName = computed(() => _ownUserStatus.value?.name || "")

  const _setOwnUserStatus = (rsvp: Rsvp) => {
    ownUserAttends.value = rsvp.reply === RsvpReply.Attend
    ownUserId.value ||= rsvp.personal_record_id.toString()
    ownUserGuestsCount.value = rsvp.guests_count ?? 0
    _ownUserStatus.value = rsvp
  }

  const updateOwnUserStatus = async (
    fetchOwnUserStatus: () => Promise<Rsvp>
  ) => {
    ownUserStatusLoading.value = true
    ownUserStatusError.value = null
    try {
      const status = await fetchOwnUserStatus()
      _setOwnUserStatus(status)
      checkAttendanceStatus(status)
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        // If the rsvp is not found set default values
        // If this handler is missing, the whole page shows an error, seems like sometimes the BE
        //  doesnt find a RSVP for the current user.
        _setOwnUserStatus({
          personal_record_id: parseInt(ownUserId.value),
          reply: RsvpReply.Pending,
          guests_count: 0,
          name: "",
          event_id: parseInt(eventId),
          url: "",
          email: "",
          avatar_url: "",
          is_coetan: false,
          type: "pending",
          finished_registration: false,
        })
        return
      }
      ownUserStatusError.value =
        "An error occurred while fetching the current user's status."
      console.error(
        "An error occurred while fetching the current user's status.",
        error
      )
    } finally {
      ownUserStatusLoading.value = false
    }
  }

  const initializeOwnUserStatus = async (personal_record_id) => {
    ownUserId.value = personal_record_id
    const fetchOwnUserStatus = () =>
      rsvpApi
        .getRsvp(eventId, personal_record_id)
        .then((response) => response.data)
    return updateOwnUserStatus(fetchOwnUserStatus)
  }

  const initializeOwnUserStatusFromDigest = async () => {
    const fetchOwnUserStatus = () =>
      digestApi.getDigestOwnRsvp(eventId).then((response) => response.data)
    return updateOwnUserStatus(fetchOwnUserStatus)
  }

  /**
   * Used for checking the attendance status of the user. If no parameter is provided, it will use the stored value.
   * @param ownUserStatusParam
   */
  const checkAttendanceStatus = (ownUserStatusParam?: Rsvp) => {
    const ownUserStatus = ownUserStatusParam || _ownUserStatus.value // Prefer the parameter, else use the stored value
    const currentEventFromStore = store.getters["event/currentEvent"] as Event

    eventAttendStatus.value = EventFunctions.determineEventAttendanceStatus(
      currentEventFromStore,
      ownUserStatus
    )
  }

  /**
   *  Used for attending or declining an event.
   */
  const updateAttendance = async (
    sendAttendanceResponse: () => Promise<Rsvp>
  ) => {
    ownUserStatusLoading.value = true
    ownUserStatusError.value = null
    try {
      const newUserStatus = await sendAttendanceResponse()
      _setOwnUserStatus(newUserStatus as Rsvp)
      checkAttendanceStatus(newUserStatus)
    } catch (error) {
      ownUserStatusError.value = "An error occurred while attending the event."
      console.error("An error occurred while attending the event.", error)
    } finally {
      ownUserStatusLoading.value = false
    }
  }

  const attend = (guestsCount = 0) => {
    const sendAttendResponse = () =>
      rsvpApi
        .updateRsvp(eventId, ownUserId.value, {
          guests_count: guestsCount,
          reply: RsvpReply.Attend,
        })
        .then((response) => response.data)

    return updateAttendance(sendAttendResponse)
  }

  const attendFromDigest = (guests_count = 0) => {
    const sendAttendResponse = () =>
      digestApi
        .upsertDigestOwnRsvp(eventId, { reply: RsvpReply.Attend, guests_count })
        .then((response) => response.data)

    return updateAttendance(sendAttendResponse)
  }

  const decline = () => {
    const sendDeclineResponse = () =>
      rsvpApi
        .updateRsvp(eventId, ownUserId.value, {
          reply: RsvpReply.Decline,
        })
        .then((response) => response.data)
    return updateAttendance(sendDeclineResponse)
  }

  const declineFromDigest = () => {
    const sendDeclineResponse = () =>
      digestApi
        .upsertDigestOwnRsvp(eventId, { reply: RsvpReply.Decline })
        .then((response) => response.data)
    return updateAttendance(sendDeclineResponse)
  }

  const setEventAttendStatusDirectly = (eventStatus: EVENT_STATUS) => {
    eventAttendStatus.value = eventStatus
  }

  return {
    ownUserId,
    ownUserStatusLoading,
    ownUserStatusError,
    ownUserAttends,
    ownUserGuestsCount,
    ownUserName,
    eventAttendStatus,
    attend,
    attendFromDigest,
    decline,
    declineFromDigest,
    checkAttendanceStatus,
    initializeOwnUserStatus,
    initializeOwnUserStatusFromDigest,
    setEventAttendStatusDirectly,
    updateAttendance,
  }
}

/**
 * This should be called in the top-level component, where the Context should be set up.
 * This probably shouldn't be called multiple times, as it will create multiple contexts.
 */
export function provideOwnUserAttendanceContext(
  eventId: string
): OwnUserAttendance {
  const concern = OwnUserAttendance(eventId)
  provide("OwnUserAttendance", concern)
  return concern
}

/**
 * This should be called in any component that needs to access the Own User Attendance Context.
 * It expects that the context has been set up by `provideOwnUserAttendanceContext`.
 */
export function useOwnUserAttendanceContext(): OwnUserAttendance {
  const concern = inject("OwnUserAttendance", null) as OwnUserAttendance
  if (concern === null) {
    throw new Error(
      "OwnUserAttendance was not found in 'useOwnUserAttendanceContext'. Did you forget to provide a context with 'provideOwnUserAttendanceContext'?"
    )
  }
  return concern
}
